import { useEffect, useMemo, useState, type CSSProperties, type JSX } from 'react';
import { useT, type T } from '../../i18n/index.js';
import { socket } from '../../net/socket.js';
import { GameRoom } from '../parts/GameRoom.js';
import { useSteadyKeys } from '../lib/steady.js';
import { useApp } from '../../state/store.js';
import './DamaBoard.css';

/**
 * S10 — the dama board. Presentation only: the engine decides legality and
 * hands this seat a list of whole legal move paths, so nothing here generates a
 * move. What the board does add is the walk-through — the engine returns a
 * capture sequence as one move, so the player clicks the chain square by square
 * and we submit the sequence that matches.
 *
 * The view is read, not re-derived. `mustCapture` and the captured squares of
 * every chain are facts the engine publishes (see `DamaView` in
 * packages/engines/src/dama/types.ts); working them out again from the geometry
 * gets flying dama wrong, because a quiet dama slide of two or more squares
 * looks exactly like a jump from the path alone.
 *
 * Kalye staging (design 1e / 2d / 4c): the board is a lattice on painted
 * plywood and the tansan sit on the *intersections*, not inside squares. That
 * is only a drawing convention — the playable cells of a checkerboard are
 * exactly the lattice points, so the engine's indices carry over untouched.
 */

/** A tansan on the board. `dama` is the crowned cap — flipped, liner up. */
export interface Cap {
  p: number;
  dama: boolean;
}

/** One whole legal tira, as the engine published it. */
export interface LegalMove {
  /** `[origin, ...landings]`. A slide has exactly two entries. */
  path: number[];
  /** Squares this move empties, in the order they are jumped. Empty for a slide. */
  captured: number[];
}

/** How the hand ended, as the engine names it. `winner: null` is a tabla. */
export interface DamaOver {
  winner: number | null;
  reason: 'todas' | 'immobilised' | 'forfeit' | 'repetition' | 'no_progress';
}

export interface DamaView {
  size: number;
  board: (Cap | null)[];
  turn: number | null;
  /** Only ever populated for the seat on the clock. */
  legal: LegalMove[];
  /** A fact about the position, so the waiting seat and the miron rail get it too. */
  mustCapture: boolean;
  /** Null while the hand is live. */
  over: DamaOver | null;
  /**
   * Plies since a capture or a man moved. The engine refunds the pot as a tabla
   * at 50, so this is the only warning a player gets that a dama-versus-dama
   * endgame is about to be declared a draw.
   */
  sinceProgress: number;
}

/** The engine's own action shape (kind + the full path it published as legal). */
interface MoveAction {
  kind: 'move';
  path: number[];
}

/** A miron arrives with this seat index — no seat, no taps, no "mine". */
const SPECTATOR_SEAT = -1;

/** Lattice pitch in table pixels. An 8×8 board spans 7 × PITCH. */
const PITCH = 30;

export function DamaBoard(): JSX.Element {
  const match = useApp((s) => s.match);
  const t = useT();
  const [path, setPath] = useState<number[]>([]);
  const [hover, setHover] = useState<number | null>(null);

  const view = useMemo(() => readDamaView(match?.view), [match?.view]);

  // Any new server event invalidates a half-built chain — the board moved.
  useEffect(() => {
    setPath([]);
    setHover(null);
  }, [match?.seq, match?.matchId]);

  if (!match || !view) {
    return <GameRoom felt={<p className="room__wait">{t('dama.wait.board')}</p>} />;
  }

  const { size, board, legal, turn, mustCapture, over, sinceProgress } = view;
  const mySeat = match.seat > SPECTATOR_SEAT ? match.seat : null;
  const watching = mySeat === null;
  const myTurn = mySeat !== null && turn === mySeat;
  /** Seat 0 fills the top rows, so it plays the board the other way up. */
  const flip = mySeat === 0;
  /** Whose caps read as the gold set: yours, or seat 0 when you are on the rail. */
  const nearSeat = mySeat ?? 0;

  const candidates = legal.filter((m) => startsWith(m.path, path));
  const sources = new Set(legal.map((m) => m.path[0]).filter(isNum));
  const nextSteps = new Set(
    candidates
      .filter((m) => m.path.length > path.length)
      .map((m) => m.path[path.length])
      .filter(isNum),
  );
  const complete = candidates.some((m) => m.path.length === path.length);

  const previewPath = hover !== null ? [...path, hover] : path;
  const previewMove = legal.find((m) => startsWith(m.path, previewPath));
  /**
   * Caps the chain drawn so far would lift. One landing takes one piece, in
   * order, so the engine's `captured` list truncated to the hops already chosen
   * is exactly the preview — no geometry, no guessing at flying jumps.
   */
  const doomed = new Set(
    previewMove ? previewMove.captured.slice(0, Math.max(previewPath.length - 1, 0)) : [],
  );
  /** Does stepping onto `idx` from the current chain take a cap? */
  const eats = (idx: number): boolean =>
    candidates.some((m) => m.path[path.length] === idx && m.captured.length > 0);

  const submit = (full: number[]): void => {
    const action: MoveAction = { kind: 'move', path: full };
    socket.send({ t: 'match.action', matchId: match.matchId, seq: match.seq, action });
    setPath([]);
    setHover(null);
  };

  const tap = (idx: number): void => {
    if (!myTurn) return;
    if (path.length === 0) {
      if (sources.has(idx)) setPath([idx]);
      return;
    }
    if (idx === path[0] && path.length === 1) {
      setPath([]);
      return;
    }
    if (!nextSteps.has(idx)) return;

    const next = [...path, idx];
    const stillGoing = legal.some((m) => startsWith(m.path, next) && m.path.length > next.length);
    const done = legal.some((m) => samePath(m.path, next));
    // A capture chain is one move to the engine: only send once it is finished.
    if (done && !stillGoing) submit(next);
    else setPath(next);
    setHover(null);
  };

  const near = board.filter((c) => c && c.p === nearSeat);
  const far = board.filter((c) => c && c.p !== nearSeat);
  const startCount = (size / 2) * 3;
  const span = (size - 1) * PITCH;

  const felt = (
    <div className="dama" style={{ '--span': `${span}px` } as CSSProperties}>
      <div className="dama__frame">
        <div className="dama__lattice" aria-label={t('dama.board.label')}>
          <svg className="dama__lines" viewBox={`0 0 ${span} ${span}`} aria-hidden="true">
            {latticeLines(size).map((l, i) => (
              <line key={i} x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]} />
            ))}
          </svg>

          <Caps
            board={board}
            size={size}
            flip={flip}
            nearSeat={nearSeat}
            picked={path[0] ?? null}
            forced={mustCapture && path.length === 0 ? sources : EMPTY_SET}
            doomed={doomed}
          />

          {Array.from({ length: size * size }, (_, d) => {
            const idx = flip ? size * size - 1 - d : d;
            const r = Math.floor(idx / size);
            const c = idx % size;
            if (((r + c) & 1) !== 1) return null;

            const dr = flip ? size - 1 - r : r;
            const dc = flip ? size - 1 - c : c;
            const cap = board[idx] ?? null;
            const hopAt = path.indexOf(idx);
            const canTap =
              myTurn &&
              (path.length === 0
                ? sources.has(idx)
                : nextSteps.has(idx) || (path.length === 1 && idx === path[0]));

            return (
              <button
                key={idx}
                className="dama__node"
                style={{ '--x': `${dc * PITCH}px`, '--y': `${dr * PITCH}px` } as CSSProperties}
                data-can={canTap}
                disabled={!canTap}
                onClick={() => tap(idx)}
                onMouseEnter={() => nextSteps.has(idx) && setHover(idx)}
                onMouseLeave={() => setHover((h) => (h === idx ? null : h))}
                aria-label={squareLabel(t, r, c, size, cap, nearSeat, watching)}
              >
                {hopAt > 0 ? <span className="dama__hop">{hopAt}</span> : null}
                {!cap && nextSteps.has(idx) ? (
                  <span className="dama__dot" data-eat={eats(idx)} />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <GameRoom
      felt={felt}
      toast={
        over ? (
          <>
            <b>{overHead(t, over, mySeat, watching)}</b> {overWhy(t, over.reason)}
          </>
        ) : nearTabla(sinceProgress) ? (
          <>
            <b>{t('dama.tabla.head')}</b> —{' '}
            {t('dama.tabla.sub', { n: DRAW_PLIES - sinceProgress })}
          </>
        ) : mustCapture && turn !== null ? (
          <>
            <b>{t('dama.force.head')}</b>
            {` — ${myTurn ? t('dama.force.mine') : t('dama.force.theirs')}`}
          </>
        ) : (
          turnLine(t, turn, myTurn, watching)
        )
      }
      actions={
        path.length > 0 ? (
          <>
            {complete ? (
              <button className="room-act" data-tone="primary" type="button" onClick={() => submit(path)}>
                {t('dama.act.play')}
              </button>
            ) : null}
            <button
              className="room-act"
              type="button"
              onClick={() => setPath(path.slice(0, -1))}
            >
              {t('dama.act.undo')}
            </button>
          </>
        ) : null
      }
      aside={
        <div className="dama__trays">
          <Tray
            label={watching ? t('dama.seat', { n: 1 }) : t('dama.tray.mine')}
            left={near.length}
            total={startCount}
            damas={near.filter((c) => c?.dama).length}
            side="mine"
          />
          <Tray
            label={watching ? t('dama.seat', { n: 2 }) : t('dama.tray.theirs')}
            left={far.length}
            total={startCount}
            damas={far.filter((c) => c?.dama).length}
            side="them"
          />
        </div>
      }
    />
  );
}

const EMPTY_SET: ReadonlySet<number> = new Set();

/**
 * Every tansan on the plywood, in one flat layer with an identity.
 *
 * Keys come from useSteadyKeys rather than the cell, so a cap that moves —
 * one hop or a whole capture chain — keeps its DOM node, and the transition
 * on .dama__capsit slides it there. Inside the node buttons, where the caps
 * used to live, a move was a remount at the far cell: a teleport. The layer
 * is pointer-inert; the node underneath is still the thing you tap, exactly
 * as before, and the dots and hop numbers stayed with it.
 */
function Caps({
  board,
  size,
  flip,
  nearSeat,
  picked,
  forced,
  doomed,
}: {
  board: (Cap | null)[];
  size: number;
  flip: boolean;
  nearSeat: number;
  picked: number | null;
  forced: ReadonlySet<number>;
  doomed: ReadonlySet<number>;
}): JSX.Element {
  const keys = useSteadyKeys(board, (cap) => cap.p);
  return (
    <div className="dama__caps" aria-hidden="true">
      {board.map((cap, idx) => {
        if (cap === null) return null;
        const r = Math.floor(idx / size);
        const c = idx % size;
        const dr = flip ? size - 1 - r : r;
        const dc = flip ? size - 1 - c : c;
        return (
          <span
            key={keys[idx] === '' ? `c${idx}` : keys[idx]}
            className="dama__capsit"
            style={{ '--x': `${dc * PITCH}px`, '--y': `${dr * PITCH}px` } as CSSProperties}
          >
            <span
              className="cap"
              data-side={cap.p === nearSeat ? 'mine' : 'them'}
              data-king={cap.dama}
              data-picked={picked === idx}
              data-forced={forced.has(idx)}
              data-doomed={doomed.has(idx)}
            >
              <span className="cap__face">★</span>
            </span>
          </span>
        );
      })}
    </div>
  );
}

/** Caps taken off, read as a column of spent tansan beside the board. */
function Tray({
  label,
  left,
  total,
  damas,
  side,
}: {
  label: string;
  left: number;
  total: number;
  damas: number;
  side: 'mine' | 'them';
}): JSX.Element {
  const t = useT();
  return (
    <div className="dama__tray" data-side={side}>
      <span className="dama__tray-label">{label}</span>
      <b>
        {left}
        {damas > 0 ? ` · ${t('dama.tray.crowned', { n: damas })}` : ''}
      </b>
      <div className="dama__pips">
        {Array.from({ length: total }, (_, i) => (
          <i key={i} data-gone={i >= left} />
        ))}
      </div>
    </div>
  );
}

/**
 * The two diagonal families that join the playable cells. Every second
 * diagonal, because the playable cells of a checkerboard sit two apart across
 * the grain — the same construction the design doc draws.
 */
function latticeLines(size: number): [number, number, number, number][] {
  const span = (size - 1) * PITCH;
  const out: [number, number, number, number][] = [];
  for (let k = -(size - 3); k <= size - 3; k += 2) {
    const d = k * PITCH;
    if (d >= 0) out.push([d, 0, span, span - d]);
    else out.push([0, -d, span + d, span]);
  }
  for (let k = 1; k <= 2 * size - 3; k += 2) {
    const t = k * PITCH;
    out.push([Math.max(0, t - span), Math.min(t, span), Math.min(t, span), Math.max(0, t - span)]);
  }
  return out;
}

/* ── Reading the engine's view ───────────────────────────────────────────── */

/**
 * The view arrives as `unknown` (the wire type must not know the rules), so it
 * is narrowed here rather than cast. Field names follow the dama engine's
 * `DamaView`; a malformed payload renders the waiting state instead of throwing
 * inside a live match.
 */
export function readDamaView(raw: unknown): DamaView | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = raw as Record<string, unknown>;
  const size = isNum(v['size']) ? v['size'] : isNum(v['boardSize']) ? v['boardSize'] : 8;
  if (size <= 0 || !Number.isInteger(size)) return null;
  const rawBoard = v['board'];
  if (!Array.isArray(rawBoard) || rawBoard.length !== size * size) return null;

  const board = rawBoard.map((cell): Cap | null => {
    if (typeof cell !== 'object' || cell === null) return null;
    const c = cell as Record<string, unknown>;
    if (!isNum(c['p'])) return null;
    // `dama` is the engine's crown flag; `k` is tolerated so a board still
    // renders if the field is ever renamed upstream.
    return { p: c['p'], dama: c['dama'] === true || c['k'] === true };
  });

  const rawLegal = v['legal'];
  const legal: LegalMove[] = Array.isArray(rawLegal)
    ? rawLegal.map((m) => readMove(m, board, size)).filter((m): m is LegalMove => m !== null)
    : [];

  const turn = isNum(v['turn']) ? v['turn'] : null;
  // Published by the engine for every viewer. Only fall back to reading it off
  // the chain list if a payload omits it.
  const mustCapture =
    typeof v['mustCapture'] === 'boolean'
      ? v['mustCapture']
      : legal.some((m) => m.captured.length > 0);
  const sinceProgress = isNum(v['sinceProgress']) ? v['sinceProgress'] : 0;

  return { size, board, legal, turn, mustCapture, over: readOver(v['over']), sinceProgress };
}

const OVER_REASONS = ['todas', 'immobilised', 'forfeit', 'repetition', 'no_progress'] as const;

function readOver(raw: unknown): DamaOver | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const o = raw as Record<string, unknown>;
  const reason = OVER_REASONS.find((r) => r === o['reason']);
  if (!reason) return null;
  return { winner: isNum(o['winner']) ? o['winner'] : null, reason };
}

function readMove(raw: unknown, board: (Cap | null)[], size: number): LegalMove | null {
  const obj = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : null;
  const rawPath = Array.isArray(raw) ? raw : obj?.['path'];
  if (!Array.isArray(rawPath)) return null;
  const path = rawPath.filter(isNum);
  if (path.length < 2) return null;

  const rawCaptured = obj?.['captured'];
  const captured = Array.isArray(rawCaptured)
    ? rawCaptured.filter(isNum)
    : capturedBy(path, board, size);
  return { path, captured };
}

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

const samePath = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((v, i) => b[i] === v);

const startsWith = (full: number[], prefix: number[]): boolean =>
  full.length >= prefix.length && prefix.every((v, i) => full[i] === v);

/**
 * The engine calls a tabla at 50 plies without a capture or a man move. Warning
 * from ten out gives a dama-versus-dama endgame time to force something rather
 * than discovering the refund after the fact.
 */
const DRAW_PLIES = 50;
const nearTabla = (sinceProgress: number): boolean =>
  sinceProgress >= DRAW_PLIES - 10 && sinceProgress < DRAW_PLIES;

function overHead(t: T, over: DamaOver, mySeat: number | null, watching: boolean): string {
  if (over.winner === null) return t('dama.over.tabla');
  if (watching) return t('dama.over.seat', { n: over.winner + 1 });
  return over.winner === mySeat ? t('dama.over.you') : t('dama.over.lost');
}

/** Dama's five endings, named. The receipt only carries the generic five. */
function overWhy(t: T, reason: DamaOver['reason']): string {
  switch (reason) {
    case 'todas':
      return t('dama.why.todas');
    case 'immobilised':
      return t('dama.why.immobilised');
    case 'forfeit':
      return t('dama.why.forfeit');
    case 'repetition':
      return t('dama.why.repetition');
    case 'no_progress':
      return t('dama.why.noprogress');
  }
}

function turnLine(t: T, turn: number | null, myTurn: boolean, watching: boolean): string {
  if (turn === null) return t('dama.turn.over');
  if (watching) return t('dama.turn.seat', { n: turn + 1 });
  return myTurn ? t('dama.turn.you') : t('dama.turn.them');
}

/**
 * Fallback only: squares the given path jumps over that hold a piece. Used when
 * a payload arrives without the engine's own `captured` list.
 */
function capturedBy(path: number[], board: (Cap | null)[], size: number): number[] {
  const out: number[] = [];
  for (let i = 1; i < path.length; i++) {
    const from = path[i - 1];
    const to = path[i];
    if (from === undefined || to === undefined) continue;
    for (const mid of squaresBetween(from, to, size)) {
      if (board[mid]) out.push(mid);
    }
  }
  return out;
}

function squaresBetween(from: number, to: number, size: number): number[] {
  const fr = Math.floor(from / size);
  const fc = from % size;
  const tr = Math.floor(to / size);
  const tc = to % size;
  const dr = Math.sign(tr - fr);
  const dc = Math.sign(tc - fc);
  if (dr === 0 || dc === 0) return [];
  const out: number[] = [];
  let r = fr + dr;
  let c = fc + dc;
  while (r !== tr && c !== tc) {
    out.push(r * size + c);
    r += dr;
    c += dc;
  }
  return out;
}

const FILES = 'abcdefgh';

function squareLabel(
  t: T,
  r: number,
  c: number,
  size: number,
  cap: Cap | null,
  nearSeat: number,
  watching: boolean,
): string {
  const sq = `${FILES[c] ?? c}${size - r}`;
  if (!cap) return sq;
  if (watching) {
    const key = cap.dama ? 'dama.sq.seat.dama' : 'dama.sq.seat';
    return t(key, { sq, n: cap.p + 1 });
  }
  const near = cap.p === nearSeat;
  const key = near
    ? cap.dama
      ? 'dama.sq.mine.dama'
      : 'dama.sq.mine'
    : cap.dama
      ? 'dama.sq.theirs.dama'
      : 'dama.sq.theirs';
  return t(key, { sq });
}
